import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useShadowHost, useTopLevelPortal } from '@homerunner-next/widget-core/runtime'
import { ELEVATED_TOKEN_SCOPE } from '../../../hooks/elevatedScope'
import { remeasureSbpAnchors } from './anchorBus'

/**
 * Sticky containment elevation (#951 hero, #1259 refine).
 *
 * Both sticky shells and their backdrops are position:fixed. Any host
 * ancestor that establishes a containing block for fixed elements
 * (transform / filter / perspective / contain:paint / will-change — standard
 * in animated hero sections) re-roots them: the pinned surface sticks to that
 * ancestor and scrolls away with the page, and the backdrop stops covering
 * the viewport — the hostile-host class #943 fixed for the anchored Where
 * listbox. The anatomy therefore renders into ONE stable portal container
 * that is re-parented imperatively between its in-flow anchor and the
 * widget-core elevated body portal while pinned.
 *
 * Re-parenting (not a portal-target switch) is the load-bearing choice:
 * switching a createPortal container UNMOUNTS and remounts its children —
 * React reconciles portals by container identity (probed for #951) — and the
 * live form would drop field drafts and focus on every crossing. Observable
 * on the refine bar, whose compression is frozen while a field is live
 * (refineSticky updateCompress); the hero compresses on pin regardless and
 * hides its fields, so there the guarantee is node identity, not the draft
 * (#1260 review, Chrome). Moving the container node carries the
 * React-managed subtree along untouched.
 * Shadow-less mounts (plain-DOM dev, no ShadowHostProvider) never get an
 * elevated target and stay in-flow, as before.
 *
 * The caller renders the anatomy in-tree inside `anchorRef` until
 * `portalReady`, then through `createPortal(anatomy, portalContainer)`.
 * Portals cannot SSR: the server markup (and the matching hydration pass)
 * renders in-tree, and the portal takes over one commit after mount — the
 * dialog.tsx a11y-mirror gate. The sentinel and spacer stay in the widget
 * tree: the sentinel drives the observer against the page scroll and the
 * spacer holds the vacated flow height — and for the refine bar both are the
 * geometry sources (positionRefine copies the spacer rect, the compress
 * threshold reads the sentinel rect): viewport coordinates that are correct
 * for the fixed shell precisely because it now sits at body level.
 *
 * `elevatedClassName`: extra container class while detached at body, for
 * dress keyed on a container class the shell leaves behind (the hero's
 * `.hfse-explorer-hero .hfse-sbp-field` 64px fields); '' for none. The
 * container always carries `hfse` + ELEVATED_TOKEN_SCOPE there (#943
 * pattern): the elevated shadow root inherits no --hfse-* tokens, so the
 * token scope re-emits the theme tokens (useExplorerCss) and the
 * --hfse-sbp-* tokens (searchbarCss, #1259) onto it. In-flow the widget
 * container provides everything and the container carries no class.
 */
export interface StickyElevationOptions {
  /** The sticky variant is configured on (hero detach / refine pin). */
  enabled: boolean
  /** The hook's pinned state — elevated while true and a target exists. */
  isSticky: boolean
  /** Extra container class while detached at body (see above); '' for none. */
  elevatedClassName: string
  /** The widget's computed CSS (useExplorerCss) for the elevated shadow root. */
  cssString: string
}

export function useStickyElevation({ enabled, isSticky, elevatedClassName, cssString }: StickyElevationOptions) {
  const widgetShadowHost = useShadowHost()
  // The elevated target is created EAGERLY: at mount, whenever the variant is
  // on and a shadow host exists, not at first pin. Cost: one extra shadow
  // root per sticky explorer for the widget's lifetime, cloning the widget's
  // stylesheets (explorer.css alone is ~770 rules); no network cost, the
  // URLs are cached. Deliberate: a target that resolves AFTER the pin moves
  // the bar with no sticky-state flip to key on (#1220 item 1, the publish
  // in the layout effect below), and gating creation on `isSticky` would
  // reintroduce that race on every crossing. Keep it eager (#1260 review).
  const portalTarget = useTopLevelPortal({
    cssString,
    enabled: enabled && !!widgetShadowHost,
  })
  const [portalContainer] = useState<HTMLDivElement | null>(() => {
    if (typeof document === 'undefined') return null
    const el = document.createElement('div')
    // Contents-transparent in BOTH homes: in-flow the shell stays a direct
    // child of its anchor's parent (a flex item of .hfse-filter-bar for the
    // hero, a child of the refine slot), elevated it adds no box either.
    el.style.display = 'contents'
    return el
  })
  const anchorRef = useRef<HTMLDivElement>(null)
  const [portalReady, setPortalReady] = useState(false)
  useEffect(() => {
    if (enabled && portalContainer) setPortalReady(true)
  }, [enabled, portalContainer])
  useLayoutEffect(() => {
    const c = portalContainer
    if (!portalReady || !c) return
    const elevated = isSticky && !!portalTarget
    const dest = elevated ? portalTarget! : anchorRef.current
    if (!dest || c.parentNode === dest) return
    // The DOM move blurs focus if it sits inside — restore it on the same
    // node (the subtree is preserved, so the element survives the move).
    const active = deepActiveElement()
    const hadFocus = !!active && c.contains(active)
    // Scope classes only while detached at body (#943 pattern), see above.
    c.className = elevated
      ? `hfse ${ELEVATED_TOKEN_SCOPE}${elevatedClassName ? ` ${elevatedClassName}` : ''}`
      : ''
    dest.appendChild(c)
    if (hadFocus && active instanceof HTMLElement) active.focus({ preventScroll: true })
    // The move changes the bar's viewport rect when the in-tree home was a
    // re-rooting ancestor, and a portal target that resolves AFTER the pin
    // moves the bar with no sticky-state flip for the caller's remeasure
    // effect to key on — publish here as well (#1220 item 1).
    remeasureSbpAnchors()
    // `enabled` is a dep on purpose: a live editor toggle off→on unmounts
    // and remounts the anchor, and the container must follow the NEW anchor
    // node (the mounted gate never resets, so nothing else re-runs this).
  }, [portalReady, portalContainer, isSticky, portalTarget, enabled, elevatedClassName])
  useEffect(() => () => portalContainer?.remove(), [portalContainer])
  return { anchorRef, portalReady, portalContainer }
}

/** The deepest focused element, walking into open shadow roots. Inside the
 *  embed the widget lives in a shadow tree, where `document.activeElement`
 *  is the widget HOST — a naive `shell.contains(document.activeElement)`
 *  is always false there. */
export function deepActiveElement(): Element | null {
  let a: Element | null = document.activeElement
  while (a?.shadowRoot?.activeElement) a = a.shadowRoot.activeElement
  return a
}
